import { Backdrop, Box, Menu, MenuItem, Typography, useTheme } from "@mui/material"
import { VideoType } from "../services/api/video"
import { useSnackbarContext } from "../services/snackbar"
import { useAuthContext } from "../services/auth"
import { UserRole } from "../services/auth/auth.types"
import { useDLQueueContext } from "../services/dlqueue"
import { uploadThumbnail } from "../utils/upload"
import { useStaticContext } from "../services/static"
import { useState } from "react"
import { httpClient } from "../services/http"

interface VideoContextMenuProps {
  video: VideoType
  onClose?: () => void
  open: boolean
  position: { x: number; y: number } | null
  loop?: boolean
  setLoop?: (loop: boolean) => void
  videoRef?: React.RefObject<HTMLVideoElement>
  onThumbnailChange?: () => void
}

export const VideoContextMenu = ({
  video,
  onClose,
  open,
  position,
  loop,
  setLoop,
  videoRef,
  onThumbnailChange,
}: VideoContextMenuProps): React.ReactElement => {
  const { open: sbOpen } = useSnackbarContext()
  const { role, userId } = useAuthContext()
  const { add: dlQueueAdd } = useDLQueueContext()
  const { playlists, reloadPlaylists, reload: reloadStatic } = useStaticContext()
  const theme = useTheme()

  const [playlistSelectorOpen, setPlaylistSelectorOpen] = useState(false)

  const handleOpenPlaylistSelector = () => {
    onClose?.()
    setPlaylistSelectorOpen(true)
  }

  const handleDownloadFrame = () => {
    if (!videoRef?.current) return
    const video = videoRef.current
    const canvas = document.createElement("canvas")
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob((blob) => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `snapshot_${video.currentTime.toFixed(2)}.png`
      document.body.appendChild(a)
      a.click()
      setTimeout(() => {
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }, 100)
    }, "image/png")
    onClose?.()
  }

  const setFrameAsThumbnail = () => {
    if (!videoRef?.current || !video) return
    const videoElt = videoRef.current
    const canvas = document.createElement("canvas")
    canvas.width = videoElt.videoWidth
    canvas.height = videoElt.videoHeight
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.drawImage(videoElt, 0, 0, canvas.width, canvas.height)
    canvas.toBlob(async (blob) => {
      if (!blob) return
      try {
        const filename = `thumbnail_${video.id}_${Math.floor(videoElt.currentTime)}.png`
        const file = new File([blob], filename, { type: blob.type || "image/png" })
        await uploadThumbnail(video, file, sbOpen)
      } catch {
        sbOpen("Une erreur s'est produite...", "error")
      } finally {
        sbOpen("Miniature modifiée avec succès", "success")
        onThumbnailChange?.()
        reloadStatic()
      }
    }, "image/png")
  }

  const handleAddVideoToPlaylist = async (playlistCode: string) => {
    try {
      await httpClient.instance.put(`playlists/${playlistCode}/addVideo/${video.id}`)
      sbOpen("Vidéo ajoutée avec succès", "success")
    } catch (error) {
      sbOpen("Erreur pendant l'ajout de la vidéo", "error")
    } finally {
      reloadPlaylists()
      setPlaylistSelectorOpen(false)
      onClose?.()
    }
  }

  return (
    <>
      <Menu
        open={open}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={position !== null ? { top: position.y, left: position.x } : undefined}
        slotProps={{
          paper: {
            sx: {
              overflow: "hidden",
              backgroundColor: theme.palette.element.contextMenu?.background ?? theme.palette.primary.main,
              backdropFilter: theme.name === "glass" ? "blur(5px)" : "unset",
            },
          },
        }}
      >
        {video.processing == 3 && (
          <>
            {setLoop && (
              <MenuItem
                onClick={() => {
                  setLoop?.(!loop)
                  onClose?.()
                }}
              >
                {`Boucler${loop ? " ✓" : ""}`}
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/video?v=${video.id}`)
                sbOpen("Lien copié !", "success")
                onClose?.()
              }}
            >
              {"Copier le lien"}
            </MenuItem>
            {videoRef?.current && (
              <MenuItem
                disabled={!videoRef.current.paused}
                onClick={() => {
                  if (videoRef.current) {
                    navigator.clipboard.writeText(
                      `${window.location.origin}/video?v=${video.id}&at=${Math.floor(videoRef.current.currentTime)}`,
                    )
                    sbOpen("Lien copié !", "success")
                  }
                  onClose?.()
                }}
              >
                {"Copier le lien à cet instant"}
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                dlQueueAdd(video)
                onClose?.()
              }}
            >
              {"Télécharger"}
            </MenuItem>
          </>
        )}
        {videoRef?.current && (
          <>
            {" "}
            <MenuItem disabled={!videoRef.current.paused} onClick={handleDownloadFrame}>
              {"Télécharger la frame"}
            </MenuItem>
            {(role === UserRole.HYRIS || role === UserRole.GEEK) && (
              <MenuItem disabled={!videoRef.current.paused} onClick={setFrameAsThumbnail}>
                {"Utiliser comme miniature"}
              </MenuItem>
            )}
          </>
        )}
        {video.processing == 3 && (role === UserRole.USER || role === UserRole.HYRIS || role === UserRole.GEEK) && (
          <MenuItem
            onClick={handleOpenPlaylistSelector}
            disabled={playlists.filter((pl) => pl.authorId === userId).length === 0}
            aria-describedby="playlistSelector"
          >
            {"Ajouter à une playlist..."}
          </MenuItem>
        )}
      </Menu>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: 2800,
          display: "flex",
          flexDirection: "column",
        }}
        open={playlistSelectorOpen}
        onClick={() => setPlaylistSelectorOpen(false)}
      >
        <Box
          maxWidth="400px"
          width="90%"
          maxHeight="70%"
          overflow="auto"
          bgcolor={theme.palette.primary.main}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography
            variant="h4"
            fontSize={"1.5rem"}
            p={2}
            borderBottom={`1px solid ${theme.palette.soft.main}`}
            sx={{ userSelect: "none" }}
          >
            {"Ajouter à une playlist"}
          </Typography>
          <Box>
            {playlists
              .filter((pl) => pl.authorId === userId)
              .sort((a, b) => a.title.localeCompare(b.title))
              .map((playlist) => (
                <Box
                  key={playlist.code}
                  onClick={() => handleAddVideoToPlaylist(playlist.code)}
                  sx={{
                    cursor: "pointer",
                    bgcolor: "transparent",
                    "&:hover": { bgcolor: theme.palette.primary.light },
                    transition: ".2s",
                  }}
                  padding={2}
                >
                  <Typography variant="h5" fontSize={"1.2rem"} overflow={"hidden"} textOverflow="ellipsis">
                    {playlist.title}
                  </Typography>
                </Box>
              ))}
          </Box>
        </Box>
      </Backdrop>
    </>
  )
}
